import type { VerificationTranslations } from "../interfaces/verification";

export const verificationTranslations: VerificationTranslations = {
  // Shell / stepper / shared actions
  step_intro: "Inicio",
  step_consent: "Consentimiento",
  step_data: "Datos",
  step_company: "Empresa",
  step_capture: "Captura",
  step_result: "Resultado",
  stepper_progress: "{{current}}/{{total}}",
  trust_footer: "Verificación segura y cifrada por Coloca",
  button_start: "Comenzar",
  button_continue: "Continuar",
  button_back: "Volver",
  button_retry: "Reintentar",
  button_close: "Cerrar",
  button_continue_later: "Continuar más tarde",

  // Intro — KYC (person)
  kyc_intro_title: "Verifica tu identidad",
  kyc_intro_subtitle: "Es rápido y seguro. Tardarás unos 2 minutos.",
  kyc_intro_needs_title: "Vas a necesitar:",
  kyc_intro_need_document: "Tu documento de identidad",
  kyc_intro_need_selfie: "Tomar una selfie",
  kyc_intro_security: "Tus datos se cifran y no se comparten con terceros sin tu permiso.",
  kyc_intro_cta: "Comenzar",

  // Intro — KYB (company)
  kyb_intro_title: "Verifica tu empresa",
  kyb_intro_subtitle: "Tardarás entre 5 y 7 minutos.",
  kyb_intro_needs_title: "Vas a necesitar:",
  kyb_intro_need_company: "Los datos fiscales de la empresa y de su representante legal",
  kyb_intro_need_documents:
    "Ten a mano la constitución y el RUT/Tax ID; te los pedirá el verificador durante la captura.",
  kyb_intro_save_note: "Puedes guardar y continuar después.",
  kyb_intro_cta: "Comenzar",

  // Resume banner
  resume_title: "Tienes una verificación a medias",
  resume_description: "Retomamos justo donde lo dejaste.",
  resume_cta: "Continuar",

  // Consent
  consent_title: "Tu consentimiento",
  consent_kyc_required: "Autorizo el tratamiento de mis datos biométricos para verificar mi identidad.",
  consent_kyb_required: "Declaro estar autorizado para representar a la empresa.",
  consent_terms_label: "Acepto los términos y condiciones.",
  consent_terms_link: "Ver términos y condiciones",
  consent_terms_new_tab: "(se abre en una pestaña nueva)",
  consent_required: "Marca esta casilla para continuar.",
  consent_cta: "Acepto y continúo",

  // Data (KYC) + confirm variant
  data_title: "Datos del documento",
  data_subtitle: "Confirma que coincidan con tu documento de identidad.",
  confirm_title: "¿Estos datos son correctos?",
  confirm_cta_confirm: "Confirmar y continuar",
  confirm_cta_edit: "Editar datos",
  confirm_field_name: "Nombre",
  confirm_field_country: "País",
  confirm_field_document_type: "Tipo de documento",

  // Company (KYB) + dedupe variant
  company_title: "Datos de la empresa",
  company_block_fiscal: "Identidad fiscal",
  company_block_fiscal_badge: "Se valida y comparte",
  company_block_representative: "Representante legal",
  company_block_contact: "Contacto",
  company_dedupe_note: "El país, el tipo fiscal y el número identifican de forma única a la empresa.",
  company_verified_title: "Esta empresa ya está registrada y verificada",
  company_verified_description: "Reutilizamos su identidad fiscal; estos campos quedan bloqueados.",
  company_verified_cta_documents: "Ver documentos",
  company_verified_cta_back: "Volver al formulario",

  // Capture — KYC (single embedded session: document + selfie/liveness)
  capture_doc_title: "Verifica tu identidad",
  capture_doc_instruction: "Sigue las indicaciones para fotografiar tu documento y tomar una selfie.",
  capture_doc_hint_light: "Buena luz",
  capture_doc_hint_complete: "Documento y rostro visibles",
  capture_doc_hint_glare: "Sin reflejos",

  // Capture — company
  capture_company_title: "Verificación de la empresa",
  capture_company_instruction:
    "El verificador te pedirá la constitución, el RUT/Tax ID y lo que requiera el nivel.",
  capture_company_hint_incorporation: "Acta de constitución",
  capture_company_hint_taxid: "RUT / Tax ID",

  // Capture — states
  capture_loading: "Iniciando verificación…",
  capture_error_title: "No pudimos iniciar la cámara",
  capture_error_description: "Revisa tu conexión e inténtalo de nuevo.",
  capture_denied_title: "Permiso de cámara bloqueado",
  capture_denied_description: "Habilita el acceso a la cámara en tu navegador para continuar.",
  capture_action_retry: "Reintentar",
  capture_action_allowed: "Ya lo permití",

  // Submit (KYB ready-to-send variant)
  submit_title: "Listo para enviar",
  submit_summary_company: "Empresa",
  submit_summary_taxid: "NIT / Tax ID",
  submit_summary_representative: "Representante legal",
  submit_cta: "Enviar a revisión",
  submit_cta_back: "Volver a la captura",

  // Processing
  processing_kyc_title: "Estamos verificando tu identidad…",
  processing_kyb_title: "Estamos verificando tu empresa…",
  processing_note: "Puedes cerrar esta ventana; te avisaremos por correo cuando esté lista.",

  // Result
  result_verified_title: "Verificación completada",
  result_verified_description: "Ya puedes continuar.",
  result_verified_description_external:
    "Tu verificación quedó registrada. Quien te la solicitó recibirá la confirmación.",
  result_verified_cta: "Ir al inicio",
  result_verified_cta_done: "Listo",
  result_verified_redirect_hint: "Te llevamos al inicio en {{seconds}} s.",
  result_verified_redirect_cancel: "Quedarme aquí",
  result_verified_redirect_announce:
    "Te llevaremos al inicio en unos segundos. Usa el botón para ir ahora, o cancela el redireccionamiento.",
  verified_close_window_note: "No tienes que hacer nada más: ya puedes cerrar esta ventana.",

  // Welcome after approval
  welcome_eyebrow: "Verificación aprobada",
  welcome_title: "¡Ya puedes usar la plataforma!",
  welcome_description_kyc:
    "Aprobamos tu verificación de identidad y ya quedó habilitado todo lo que dependía de ella.",
  welcome_description_kyb:
    "Aprobamos la verificación de tu empresa y ya quedó habilitado todo lo que dependía de ella.",
  welcome_choose: "¿Por dónde quieres empezar?",
  welcome_shortcuts_label: "Accesos rápidos",
  welcome_close: "Cerrar",
  welcome_shortcut_payins_description: "Crea un cobro y compártelo con quien te va a pagar.",
  welcome_shortcut_payouts_description: "Envía dinero a cuentas bancarias y llaves.",
  welcome_shortcut_cards_description: "Emite tarjetas para tu equipo o tus clientes.",
  welcome_shortcut_wallets_description: "Consulta tus saldos y movimientos en cada moneda.",
  welcome_shortcut_security_title: "Seguridad",
  welcome_shortcut_security_description: "Elige cómo vas a confirmar pagos y movimientos con tarjetas.",
  welcome_shortcut_crypto_description: "Compra USDT con el saldo de tu billetera en pesos.",
  welcome_shortcut_payout_batches_description: "Paga a muchas personas a la vez con una hoja de cálculo.",
  welcome_shortcut_clients_description: "Registra a las personas y empresas con las que operas.",
  result_review_title: "Estamos revisando tu verificación",
  result_review_description: "Te notificaremos por correo cuando tengamos el resultado.",
  result_review_cta: "Volver",
  result_rejected_title: "No pudimos completar la verificación",
  result_rejected_description: "Revisa el motivo e inténtalo de nuevo.",
  result_rejected_cta: "Reintentar verificación",
  result_expired_title: "Este enlace expiró",
  result_expired_description: "Solicita uno nuevo para continuar.",
  result_expired_cta: "Solicitar uno nuevo",

  // Transversal states
  invalid_title: "Enlace no válido",
  invalid_description: "El enlace de verificación no es válido o ya no está disponible.",
  already_title: "Ya estás verificado",
  already_description: "Tu verificación se completó el {{date}}.",
  mobile_title: "Continúa desde tu móvil",
  mobile_description: "Escanea el código para terminar la captura en tu teléfono.",
  error_generic_title: "Algo salió mal",
  error_generic_description: "No pudimos completar la operación. Inténtalo de nuevo.",

  // Rejection reasons
  rejection_document_quality:
    "La foto del documento no se ve con claridad. Inténtalo de nuevo con buena luz.",
  rejection_document_invalid: "El documento no es válido o no se reconoce.",
  rejection_document_incomplete: "Faltan datos del documento. Sube el documento completo.",
  rejection_selfie_mismatch: "La selfie no coincidió con el documento. Inténtalo de nuevo con buena luz.",
  rejection_data_mismatch: "Los datos no coinciden con el documento.",
  rejection_duplicate: "Ya existe una verificación para esta identidad.",
  rejection_compliance: "No pudimos aprobar la verificación. Contacta a soporte.",
  rejection_additional_info_required: "Necesitamos información adicional para continuar.",
  rejection_generic: "No pudimos completar la verificación. Inténtalo de nuevo.",

  // Documents (§6B)
  documents_title: "Documentos",
  documents_signed_note: "Las descargas usan enlaces firmados temporales.",
  documents_download: "Descargar",
  documents_empty_title: "Aún no hay documentos",
  documents_empty_description: "Aparecerán cuando la verificación se complete.",

  // Onboarding (marco A)
  onboarding_title: "Verificación de la cuenta",
  onboarding_description: "Completa la verificación de tu cuenta para empezar a operar.",
  banner_cta: "Verificar ahora",
  banner_cta_resume: "Continuar verificación",
  banner_view_details: "Ver detalles",
  banner_blocked_prefix: "Mientras tu cuenta no esté verificada no podrás:",
  banner_in_review_title: "Tu verificación está en revisión",
  banner_in_review_description: "Te avisaremos por correo cuando tengamos el resultado.",
  banner_rejected_title: "No pudimos aprobar tu verificación",
  banner_expired_title: "Tu verificación expiró",
  banner_expired_description: "Vuelve a iniciar la verificación para seguir operando.",

  // Onboarding — KYC vs KYB choice
  choose_title: "¿Cómo operas en Coloca?",
  choose_description:
    "Elige cómo quieres verificar tu cuenta. La verificación que elijas es la que habilita tus operaciones.",
  choose_person_title: "Como persona natural",
  choose_person_title_mx: "Como persona física",
  choose_person_description: "Verificaremos tu identidad con tu documento y una selfie (KYC).",
  choose_business_title: "Como empresa",
  choose_business_description: "Verificaremos la empresa y a su representante legal (KYB).",
  choose_note: "La elección se hace una sola vez: queda fija al crear la identidad.",
  choose_cta: "Continuar",
  replace_warning_title: "Vas a verificar tu cuenta con otra identidad",
  replace_warning_description:
    "Tu verificación actual dejará de aplicar. Elige cómo operarás a partir de ahora; los envíos de dinero quedan bloqueados hasta que la nueva verificación esté aprobada.",

  // INTERNAL — account verification admin
  admin_title: "Verificación de cuentas",
  admin_description:
    "Estado de verificación de una organización y exoneración del cooldown de cambio de identidad.",
  admin_scope_label: "Organización",
  admin_scope_placeholder: "Busca por nombre, correo o id",
  admin_scope_loading: "Cargando organizaciones…",
  admin_scope_empty: "Sin resultados",
  admin_scope_searching: "Buscando…",
  admin_scope_search_error: "No pudimos buscar organizaciones.",
  admin_scope_catalog_error: "No pudimos cargar las organizaciones.",
  admin_scope_catalog_truncated: "Se muestran las primeras organizaciones; busca para llegar al resto.",
  admin_scope_global_option: "Ninguna organización",
  admin_scope_global_hint: "Selecciona la organización que te pidió el cambio.",
  admin_scope_org_hint: "Estás viendo la verificación de esta organización.",
  admin_pick_org: "Elige una organización para ver su verificación.",
  admin_load_error: "No pudimos cargar la verificación de esta organización.",
  admin_type_label: "Tipo",
  admin_status_label: "Estado",
  admin_change_label: "Cambio de identidad",
  admin_change_open: "Puede cambiar de identidad ahora.",
  admin_change_blocked: "Bloqueado hasta el {{date}} (reemplazó una identidad verificada hace poco).",
  admin_waive: "Exonerar cooldown",
  admin_waive_confirm_title: "¿Exonerar el cooldown?",
  admin_waive_confirm_description:
    "La organización podrá verificar su cuenta con otra identidad de inmediato. Queda registrado y se avisa a operaciones por Discord.",
  admin_waive_cancel: "Cancelar",
  admin_waive_done: "Cooldown exonerado.",
  admin_retry: "Reintentar",
  admin_link_open: "Vincular verificación existente",
  admin_link_title: "Vincular una verificación existente",
  admin_link_description:
    "Para una organización que ya se verificó fuera de la plataforma: busca su expediente, revisa los datos y confirma con tu código. Queda verificada sin repetir el onboarding.",
  admin_link_id_label: "ID del expediente KYC",
  admin_link_id_placeholder: "Pega el ID del expediente aprobado",
  admin_link_search: "Buscar",
  admin_link_searching: "Buscando el expediente…",
  admin_link_summary_title: "El expediente",
  admin_link_subject_business: "Empresa (KYB)",
  admin_link_subject_individual: "Persona (KYC)",
  admin_link_field_kind: "Tipo",
  admin_link_field_name: "Nombre",
  admin_link_field_status: "Estado",
  admin_link_field_reviewed_at: "Aprobado el",
  admin_link_field_documents: "Documentos",
  admin_link_action_title: "Qué va a pasar",
  admin_link_action_create_identity:
    "Se crea la identidad corporativa de {{name}} con los datos de abajo y queda verificada con este expediente.",
  admin_link_action_link_existing_identity:
    "La identidad que ya existe con ese documento pasa a ser la de {{name}} y queda verificada con este expediente.",
  admin_link_action_repoint:
    "La verificación pendiente de {{name}} se reemplaza por este expediente y queda verificada.",
  admin_link_action_already_linked:
    "Este expediente ya es la verificación de {{name}}. Vincularlo de nuevo solo vuelve a aplicar su estado.",
  admin_link_current_title: "Identidad actual de la organización",
  admin_link_current_document: "Documento",
  admin_link_blockers_title: "No se puede vincular",
  admin_link_warnings_title: "Revisa antes de confirmar",
  admin_link_blocker_applicant_not_found:
    "No hay ningún expediente con ese ID. Revisa que lo copiaste completo.",
  admin_link_blocker_applicant_not_approved:
    "El expediente no está aprobado. Solo se puede vincular uno aprobado.",
  admin_link_blocker_applicant_review_changed:
    "La revisión del expediente cambió mientras vinculabas. Búscalo de nuevo para ver su estado actual.",
  admin_link_blocker_applicant_linked_elsewhere:
    "El expediente ya es la verificación de otra identidad ({{identity_id}}).",
  admin_link_blocker_applicant_owned_by_other_organization:
    "El expediente se abrió para otra organización ({{organization_id}}).",
  admin_link_blocker_subject_kind_mismatch:
    "El tipo no coincide: el expediente y la identidad tienen que ser los dos de empresa o los dos de persona.",
  admin_link_blocker_corporate_identity_already_verified:
    "La organización ya está verificada. Una verificación aprobada no se reemplaza desde aquí.",
  admin_link_blocker_identity_has_other_verification:
    "Ya existe una identidad con ese documento ({{identity_id}}) y tiene otra verificación.",
  admin_link_blocker_document_mismatch:
    "El número de documento no es el del expediente ({{applicant_documents}}).",
  admin_link_blocker_organization_kyc_provider_unsupported:
    "La organización se verifica con otro proveedor; este vínculo no aplica.",
  admin_link_blocker_link_stale:
    "La identidad de la organización cambió mientras vinculabas. Busca el expediente de nuevo.",
  admin_link_blocker_identity_required: "Completa los datos de la identidad antes de confirmar.",
  admin_link_blocker_identity_not_allowed:
    "La organización ya tiene identidad corporativa. Busca el expediente de nuevo.",
  admin_link_blocker_unknown: "No se pudo vincular ({{code}}).",
  admin_link_warning_name_mismatch:
    "El nombre del expediente ({{applicant_name}}) no se parece al de la identidad ({{identity_name}}).",
  admin_link_warning_country_mismatch:
    "El expediente es de {{applicant_country}} y la organización de {{organization_country}}.",
  admin_link_warning_applicant_without_document:
    "El expediente no trae número de documento para compararlo con la identidad.",
  admin_link_warning_applicant_created_for_other_identity:
    "El expediente se abrió para otra identidad ({{identity_id}}).",
  admin_link_warning_unknown: "Revisa: {{code}}.",
  admin_link_missing: "El expediente no trae {{count}} datos obligatorios. Complétalos en el formulario.",
  admin_link_form_title: "Datos de la identidad",
  admin_link_form_hint:
    "Prellenados desde el expediente. El número de documento tiene que ser el del expediente.",
  admin_link_reason_label: "Motivo",
  admin_link_reason_placeholder: "Quién lo pidió, cómo se comprobó, ticket…",
  admin_link_reason_hint: "Queda en la auditoría junto con tu usuario.",
  admin_link_reason_too_short: "Escribe al menos 10 caracteres.",
  admin_link_submit: "Vincular y verificar",
  admin_link_cancel: "Cancelar",
  admin_link_confirm_title: "¿Vincular esta verificación?",
  admin_link_confirm_description:
    "{{name}} queda verificada con este expediente y podrá operar. Se registran tu usuario y el motivo.",
  admin_link_confirm: "Confirmar con código",
  admin_link_two_factor_required:
    "Para vincular una verificación necesitas la app de autenticación activada.",
  admin_link_two_factor_description:
    "Ingresa el código de tu app de autenticación para verificar a {{name}}.",
  admin_link_linking: "Vinculando y verificando…",
  admin_link_done: "{{name}} quedó verificada.",
  admin_link_provider_unavailable:
    "No se pudo consultar al proveedor de verificación. Inténtalo de nuevo en unos minutos.",

  // Capability matrix
  capability_payouts_create: "Enviar dispersiones",
  capability_payins_create: "Recibir pagos",
  capability_wallets_recharge: "Recargar billeteras",
  capability_swaps_create: "Convertir entre billeteras",
  capability_cards_issue: "Emitir tarjetas",

  // Dispersions gate
  payouts_blocked_title: "Verifica tu cuenta para enviar dinero",
  payouts_blocked_description:
    "Las dispersiones se habilitan cuando la verificación de tu cuenta esté aprobada. Puedes retomarla donde la dejaste.",
  payouts_blocked_in_review_description:
    "Tu verificación está en revisión. Podrás enviar dinero cuando esté aprobada.",
  payouts_blocked_back_wallets: "Volver a billeteras",
  payouts_blocked_start_description:
    "Elige si operas como persona natural o como empresa y completa la verificación para poder enviar dinero.",

  // Status pill labels
  status_pending: "Pendiente",
  status_in_review: "En revisión",
  status_verified: "Verificado",
  status_rejected: "Rechazado",
  status_expired: "Expirado",

  // Marco C — identity verification section / documents
  verify_action: "Iniciar verificación",
  verify_dialog_title: "Verificación",
  link_generate: "Generar enlace",
  link_copied: "Enlace copiado al portapapeles",
  documents_report_label: "Reporte de verificación",
  photo_guide_eyebrow: "Antes de tomar las fotos",
  photo_guide_title: "Así deben verse tus fotos",
  photo_guide_lead:
    "Si se ven así, tu verificación se aprueba sola. Si no, pasa a revisión manual y puede tardar semanas.",
  photo_guide_tip_light_title: "Buena luz",
  photo_guide_tip_light_body: "De frente y pareja, sin flash, reflejos ni sombras.",
  photo_guide_tip_place_title: "Un lugar despejado",
  photo_guide_tip_place_body: "Una pared lisa detrás de ti y una mesa oscura para el documento.",
  photo_guide_tip_document_title: "Tu documento físico",
  photo_guide_tip_document_body: "Original y vigente. No sirven fotocopias ni versiones digitales.",
  photo_guide_document_title: "Tu documento",
  photo_guide_document_caption:
    "Las cuatro esquinas a la vista y todo legible. Frente y reverso en fotos separadas; el pasaporte, abierto en una sola foto.",
  photo_guide_selfie_title: "Tu selfie",
  photo_guide_selfie_caption: "Solo tu cara, centrada y de frente, con los ojos abiertos.",
  photo_guide_example_ok: "Así sí",
  photo_guide_example_corner: "Esquina fuera de la foto",
  photo_guide_example_glare: "Reflejo o flash",
  photo_guide_example_angle: "En ángulo o torcida",
  photo_guide_example_blurry: "Borrosa",
  photo_guide_example_contrast: "Fondo sin contraste",
  photo_guide_example_offcenter: "Descentrada o cortada",
  photo_guide_example_tilted: "Cabeza inclinada",
  photo_guide_example_glasses: "Con gafas o gorra",
  photo_guide_example_holding: "Con el documento en la mano",
  photo_guide_example_crowd: "Otra persona en la foto",
  photo_guide_rejected_note: "Si una foto es rechazada, no pasa nada: toma una nueva y vuelve a subirla.",
  photo_guide_cta: "Empezar",
};
